import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import ConditionIcons from 'features/Weather/components/ConditionIcons/ConditionIcons';
import TimePeriodCard from 'features/Weather/components/TimePeriodCard/TimePeriodCard';
import { getForecastDateLabel } from 'features/Weather/utils/forecastDate.utils';
import useLocation from 'hooks/useLocation';
import { useId, useMemo } from 'react';
import { Container } from 'react-bootstrap';
import { useGetDetailedForecastQuery } from 'services/weather/weather.api';
import WeatherCard from 'features/Weather/components/WeatherCard/WeatherCard';
import { weatherDetails } from '../../config/forecast.config';
import 'features/Weather/assets/weatherDataDisplay.css';
import './assets/detailedForecast.scss';
import { timeOfDayDetails } from './config/detailedForecast.config';
import {
  getForecastByTimeOfDay,
  getForecastForDay,
  getTimeOfDayLabel,
  getTimeOfDaySections,
} from './utils/detailedForecast.utils';
import EmptyState from 'components/EmptyState/EmptyState';

const DetailedForecast = ({ selectedDay }) => {
  const headingId = useId();
  const { locations } = useLocation();
  const effectiveLocation = locations.effectiveLocation;
  const { data: detailedForecast, isLoading: loadingDetailedForecast } =
    useGetDetailedForecastQuery(effectiveLocation);

  const filteredForecast = useMemo(() => {
    return getForecastForDay(detailedForecast, selectedDay);
  }, [detailedForecast, selectedDay]);

  const timeOfDayForecast = useMemo(() => {
    return getForecastByTimeOfDay(filteredForecast);
  }, [filteredForecast]);

  if (loadingDetailedForecast) return <WidgetLoadingAnimation />;

  if (!filteredForecast.length) {
    return (
      <Container
        fluid
        className="weather-data-display detailed-forecast d-flex flex-column h-100 user-select-none px-0"
      >
        <EmptyState
          title="Detailed Forecast"
          message="No detailed forecast data available for the selected day."
        />
      </Container>
    );
  }

  return (
    <Container
      fluid
      className="weather-data-display detailed-forecast d-flex flex-column gap-4 h-100 user-select-none px-0"
    >
      <div className="d-flex flex-column gap-4">
        <section aria-labelledby={headingId} className="d-flex flex-column gap-3">
          <div className="pb-2 border-bottom weather-data-display-heading">
            <h2 id={headingId} className="m-0 fw-bold lh-sm weather-data-display-title">
              {getForecastDateLabel(filteredForecast[0]?.iso8601, 'Detailed Forecast') ??
                'Detailed Forecast'}
            </h2>
          </div>
          <div className="d-grid gap-3 weather-time-period-grid">
            {filteredForecast.map((forecast) => (
              <TimePeriodCard
                key={forecast?.iso8601}
                data={forecast}
                title={getTimeOfDayLabel(forecast)}
                subtitleIcon={
                  <ConditionIcons conditionId={parseInt(forecast?.icon || '0', 10)} size={100} />
                }
                subtitle={
                  <span className="fw-bold lh-1 detailed-forecast-temperature">
                    {`${forecast?.temperature ?? '--'}°F`}
                  </span>
                }
                details={timeOfDayDetails}
                detailsContainerClassName="gap-3"
                detailContainerClassName="justify-content-center"
                detailLabelClassName="visually-hidden"
              />
            ))}
          </div>
        </section>
        <div className="d-grid gap-3 detailed-forecast-card-grid">
          {getTimeOfDaySections(timeOfDayForecast).map(([title, forecast]) => {
            if (!forecast) return null;
            return (
              <WeatherCard
                key={title}
                weatherObj={forecast}
                title={title}
                headerIcon={
                  <ConditionIcons conditionId={parseInt(forecast?.icon || '0', 10)} size={25} />
                }
                details={weatherDetails}
              />
            );
          })}
        </div>
      </div>
    </Container>
  );
};

export default DetailedForecast;
